import { createResourceCollection } from '@/request'
import { createWrapSingularResponseInObjectMiddleware } from '@/request/middleware/createWrapSingularResponseInObjectMiddleware'
import { TeslaFleetAuthentication } from '@/types'
import {
  DeviceAgnosticFleetAuthentication,
  RequiredEnergyScopes,
  ScopedFleetAuthentication,
} from '@/types/teslaFleetAuthentication'
import { match } from 'ts-pattern'

export const teslaFleetAuthenticationStoreKey = 'teslaFleetAuthentication'
export const scopedFleetAuthenticationStoreKey = 'scopedFleetAuthentication'
export const deviceAgnosticFleetAuthenticationStoreKey =
  'deviceAgnosticFleetAuthentication'
export const requiredEnergyScopesStoreKey = 'requiredEnergyScopes'

export const teslaFleetAuthenticationCollection =
  createResourceCollection<TeslaFleetAuthentication>({
    name: 'teslaFleetAuthentication',
    apiConfig: {
      path: 'tesla-fleet-authentication',
    },
    selector: (state) => state[teslaFleetAuthenticationStoreKey],
  })

export const teslaFleetAuthenticationReducer =
  teslaFleetAuthenticationCollection.slice.reducer

export const scopedFleetAuthenticationCollection =
  createResourceCollection<ScopedFleetAuthentication>({
    name: 'scopedFleetAuthentication',
    selector: (state) => state[scopedFleetAuthenticationStoreKey],
  })

export const scopedFleetAuthenticationReducer =
  scopedFleetAuthenticationCollection.slice.reducer

export const deviceAgnosticFleetAuthenticationCollection =
  createResourceCollection<DeviceAgnosticFleetAuthentication>({
    name: 'deviceAgnosticFleetAuthentication',
    apiConfig: {
      path: 'tesla-fleet-authentication/device-agnostic',
    },
    selector: (state) => state[deviceAgnosticFleetAuthenticationStoreKey],
  })

export const deviceAgnosticFleetAuthenticationReducer =
  deviceAgnosticFleetAuthenticationCollection.slice.reducer

export const requiredEnergyScopesCollection =
  createResourceCollection<RequiredEnergyScopes>({
    name: 'requiredEnergyScopes',
    apiConfig: {
      middlewares: [
        // wrap the array response in an object where the id corresponds to the
        // device type
        createWrapSingularResponseInObjectMiddleware(
          'scopes',
          (requestData, data) => {
            const path = requestData.endpoint
            if (typeof path !== 'string') {
              throw new Error('Path for request data is not a string')
            }
            return match(path)
              .when(
                (p) => p.includes('energy-systems'),
                () => 'energySystem',
              )
              .otherwise(() => 'vehicle')
          },
        ),
      ],
    },
    selector: (state) => state[requiredEnergyScopesStoreKey],
  })

export const requiredEnergyScopesReducer =
  requiredEnergyScopesCollection.slice.reducer

export const useFetchEnergySystemRequiredScopes = () => {
  const { data, isLoadingOrIdle, error } =
    requiredEnergyScopesCollection.useFetch(
      '/tesla-fleet-authentication/energy-systems/required-scopes',
    )

  if (data.length > 1) {
    throw new Error(
      'Expected 1 energy system required scopes, got ' + data.length,
    )
  }

  return {
    data: data?.[0]?.scopes ?? [],
    isLoading: isLoadingOrIdle,
    error,
  }
}
